import { useEffect, useState, useCallback } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  IconExpand,
  IconLoader,
  PermissionEnum,
  ResponseError,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetFeesListQuery } from './store/services';
import FeeBlock from '../../components/fees/fee-block';

import { FeesRoutes } from '../../routes';

import cn from './fees.module.css';

const FeesList = () => {
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [errorList, setErrorList] = useState('');

  const { fees } = useAppSelector((state) => state.fees.list);
  const { activePermissions, paymentTypes } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const [getFeesList, { isLoading }] = useLazyGetFeesListQuery();

  const getFeesListAction = useCallback(async () => {
    setErrorList('');

    const response = (await getFeesList()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, []);

  const onCollapse = (value: string) => {
    let nextCollapsed = ([] as string[]).concat(collapsed);

    if (nextCollapsed.find((item) => item === value)) {
      nextCollapsed = nextCollapsed.filter((item) => item !== value);
    } else {
      nextCollapsed.push(value);
    }

    setCollapsed(nextCollapsed);
  };

  const onCollapseAll = () => {
    let collapsedList: string[];

    if (collapsed.length === fees?.length) {
      collapsedList = [];
    } else {
      collapsedList = fees?.map((item) => `${item.type}-${item.subType}`);
    }

    setCollapsed(collapsedList);
  };

  useEffect(() => {
    getFeesListAction();
  }, [getFeesListAction]);

  useEffect(() => {
    let title = 'Fee types';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.feesLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
        {!errorList && (
          <>
            <div className={cn.feesCollapseButtonWrapper}>
              <button
                type="button"
                className={cn.feesCollapseButton}
                onClick={onCollapseAll}
              >
                {collapsed.length === fees?.length
                  ? 'Expand all'
                  : 'Collapse all'}
              </button>
            </div>
            {fees?.map((item) => (
              <div key={`${item.type}-${item.subType ? item.subType : 'fee'}`}>
                {(item?.list?.length as number) > 0 && (
                  <FeeBlock
                    key={`${item.type}-${item.subType}`}
                    item={item}
                    collapsed={Boolean(
                      collapsed.find(
                        (value) => value === `${item.type}-${item.subType}`,
                      ),
                    )}
                    paymentTypes={paymentTypes}
                    setCollapsed={onCollapse}
                  />
                )}
              </div>
            ))}
          </>
        )}
      </>
    );
  }

  return (
    <div className={cn.feesBottom}>
      <div className={cn.feesTopWrapper}>
        <div className={cn.feesTitle}>Fee types</div>
        {activePermissions?.includes(
          PermissionEnum.FEES_TYPES_CREATE_ACTION,
        ) && (
          <div className={cn.feesTop}>
            <NavLink to={FeesRoutes.FEES_CREATE} className={cn.feesCreateLink}>
              <div className={cn.feesCreateLinkContent}>
                <IconExpand className={cn.feesCreateLinkIcon} />
                <span>Add fee type</span>
              </div>
            </NavLink>
          </div>
        )}
      </div>
      {content}
      <Outlet />
    </div>
  );
};

export default FeesList;
